/**
 * Curated CSS-gradient wallpaper library for the wheel / segment builder.
 *
 * WHY THIS EXISTS
 * ----------------
 * The wallpaper picker's gradient presets are stored as raw CSS `background`
 * strings in `wallpaper_settings.bg_wallpaper` (currentBg === 3). The render
 * path applies that string DIRECTLY as a CSS `background` value
 * (see ViewWheel/styles.js + MobileView/styles.js), so any valid CSS gradient
 * — linear, radial, or conic, single- or multi-stop — renders instantly with
 * ZERO network requests. That makes a curated gradient library the cheapest,
 * fastest way to fix both "the preset list is too small" and "wallpapers load
 * slowly" at once: gradients have no image to download.
 *
 * DATA CONTRACT (unchanged): each entry is a CSS background string. Selecting
 * one calls handleGradientColor(string) -> setBgGradient(string), which stores
 * the string verbatim. No schema change; image-based presets keep working.
 *
 * FOR FABLE (aesthetic curation): this is a STARTER library, grouped by mood so
 * the final palette is easy to curate/extend. Add, reorder, or replace entries
 * within each group; the only hard requirement is that each value is a valid CSS
 * gradient string. New named groups are fine — just add them to GRADIENT_GROUPS
 * and they flow through to `gradients` automatically.
 *
 * Each entry: { value: <css gradient string>, name: <short label for tooltip> }
 */

/* ---------------------------------------------------------------------------
 * The original 39 presets, preserved verbatim (order unchanged) so existing
 * wheels that reference these exact strings keep matching the swatch border.
 * Kept as a flat list because they predate the mood grouping.
 * ------------------------------------------------------------------------- */
export const LEGACY_GRADIENTS = [
  "linear-gradient(45deg, #F7FED5, #CCFEFF, #FDCFFE, #F5EEDE)",
  "linear-gradient(90deg, #C04848 0%, #480048 100%)",
  "linear-gradient(90deg, #232526 0%, #414345 100%)",
  "linear-gradient(90deg, #536976 0%, #292E49 100%)",
  "linear-gradient(90deg, #948E99 0%, #2E1437 100%)",
  "linear-gradient(90deg, #360033 0%, #0B8793 100%)",
  "linear-gradient(90deg, #F7941E 0%, #004E8F 100%)",
  "linear-gradient(90deg, #5C258D 0%, #4389A2 100%)",
  "linear-gradient(90deg, #29323C 0%, #485563 100%)",
  "linear-gradient(90deg, #7B7E86 0%, #9A9EA5 100%)",
  "linear-gradient(90deg, #E52D27 0%, #B31217 100)",
  "linear-gradient(90deg, #9400D3 0%, #4B0082 100%)",
  "linear-gradient(90deg, #FF00CC 0%, #333399 100%)",
  "linear-gradient(90deg, #0576BE 0%, #1C1EA3 100%)",
  "linear-gradient(90deg, #0083FE 0%, #00FFF0 100%)",
  "linear-gradient(90deg, #093028 0%, #237A57 100%)",
  "linear-gradient(90deg, #28C22E 0%, #197225 100%)",
  "linear-gradient(90deg, #73E49A 0%, #1BA049 98.96%)",
  "linear-gradient(90deg, #FFC500 0%, #C21500 100%)",
  "linear-gradient(90deg, #EEF21B 0%, #FFC412 100%)",
  "linear-gradient(90deg, #FFEFEF 0%, #FF7C7C 100%)",
  "linear-gradient(90deg, #D4BBFF 0%, #BE95FF 98.96%)",
  "linear-gradient(90deg, #F4C4F3 0%, #FC67FA 100%)",
  "linear-gradient(90deg, #0062FF 0%, #6FA3FF 100%)",
  "linear-gradient(90deg, #2980B9 0%, #6DD5FA 50%, #FFFFFF 100%)",
  "linear-gradient(90deg, #64965E 0%, #81FF8A 100%)",
  "linear-gradient(90deg, #D4FC79 0%, #96E6A1 100%)",
  "linear-gradient(90deg, #F8B500 0%, #FCEABB 100%)",
  "linear-gradient(90deg, #FEFDD1 0%, #FFFC00 97.4%)",
  "linear-gradient(90deg, #FFF1B0 0%, #FFC994 100%)",
  "linear-gradient(90deg, #9FD2BA 0%, #CD70B9 100%)",
  "linear-gradient(90deg, #E0EAFC 0%, #CFDEF3 100%)",
  "linear-gradient(90deg, #E1F5C4 0%, #EDE574 100%)",
  "linear-gradient(90deg, #C6FFDD 0%, #FBD786 50%, #F7797D 100%)",
  "linear-gradient(90deg, #757F9A 0%, #D7DDE8 100%)",
  "linear-gradient(90deg, #70E1F5 0%, #FFD194 100%)",
  "linear-gradient(90deg, #BFE9FF 0%, #FF6E7F 100%)",
  "linear-gradient(90deg, #FFFFFF 0%, #FFEFBA 100%)",
  "linear-gradient(90deg, #F1F2B5 0%, #91F2FF 100%)",
];

/* ---------------------------------------------------------------------------
 * NEW curated additions, grouped by mood. ~36 fresh presets spanning
 * linear / radial / conic gradients + soft repeating patterns. All render
 * instantly (no network). Fable can reorder / replace within each group.
 * ------------------------------------------------------------------------- */
export const GRADIENT_GROUPS = [
  {
    id: "warm-sunset",
    label: "Warm & Sunset",
    gradients: [
      { value: "linear-gradient(135deg, #FF9A9E 0%, #FAD0C4 100%)", name: "Peach Glow" },
      { value: "linear-gradient(135deg, #FFB347 0%, #FF5E62 100%)", name: "Mango Sunset" },
      { value: "linear-gradient(120deg, #F6D365 0%, #FDA085 100%)", name: "Golden Hour" },
      { value: "radial-gradient(circle at 30% 20%, #FFE29F 0%, #FFA99F 55%, #FF719A 100%)", name: "Coral Bloom" },
      { value: "linear-gradient(135deg, #F83600 0%, #FE8C00 100%)", name: "Ember" },
      { value: "conic-gradient(from 210deg at 50% 50%, #FFD194, #FF867A, #FFD194)", name: "Sunwheel" },
    ],
  },
  {
    id: "cool-ocean",
    label: "Cool & Ocean",
    gradients: [
      { value: "linear-gradient(135deg, #4FACFE 0%, #00F2FE 100%)", name: "Aqua Sky" },
      { value: "linear-gradient(135deg, #2193B0 0%, #6DD5ED 100%)", name: "Deep Sea" },
      { value: "radial-gradient(circle at 70% 30%, #5EE7DF 0%, #2C82C9 100%)", name: "Lagoon" },
      { value: "linear-gradient(160deg, #0F2027 0%, #203A43 50%, #2C5364 100%)", name: "Midnight Tide" },
      { value: "linear-gradient(135deg, #A1FFCE 0%, #FAFFD1 100%)", name: "Seafoam" },
      { value: "conic-gradient(from 140deg at 50% 50%, #00C9FF, #92FE9D, #00C9FF)", name: "Reef Spin" },
    ],
  },
  {
    id: "lush-green",
    label: "Lush & Green",
    gradients: [
      { value: "linear-gradient(135deg, #56AB2F 0%, #A8E063 100%)", name: "Meadow" },
      { value: "linear-gradient(135deg, #11998E 0%, #38EF7D 100%)", name: "Emerald Flow" },
      { value: "radial-gradient(circle at 25% 25%, #C2E59C 0%, #64B3F4 100%)", name: "Spring Field" },
      { value: "linear-gradient(160deg, #134E5E 0%, #71B280 100%)", name: "Forest Depth" },
      { value: "linear-gradient(135deg, #DCE35B 0%, #45B649 100%)", name: "Citron Leaf" },
      { value: "conic-gradient(from 90deg at 50% 50%, #43E97B, #38F9D7, #43E97B)", name: "Mint Wheel" },
    ],
  },
  {
    id: "vibrant-pop",
    label: "Vibrant & Pop",
    gradients: [
      { value: "linear-gradient(135deg, #FC466B 0%, #3F5EFB 100%)", name: "Electric" },
      { value: "linear-gradient(135deg, #DA22FF 0%, #9733EE 100%)", name: "Ultraviolet" },
      { value: "radial-gradient(circle at 50% 0%, #FF6FD8 0%, #3813C2 100%)", name: "Neon Dome" },
      { value: "linear-gradient(135deg, #F857A6 0%, #FF5858 100%)", name: "Hot Pink" },
      { value: "conic-gradient(from 0deg at 50% 50%, #FF0080, #FF8C00, #FFED00, #00FF87, #00C2FF, #FF0080)", name: "Spectrum Wheel" },
      { value: "linear-gradient(135deg, #FFD200 0%, #F7971E 100%)", name: "Tangerine" },
    ],
  },
  {
    id: "dreamy-pastel",
    label: "Dreamy & Pastel",
    gradients: [
      { value: "linear-gradient(135deg, #E0C3FC 0%, #8EC5FC 100%)", name: "Lavender Mist" },
      { value: "linear-gradient(135deg, #FBC2EB 0%, #A6C1EE 100%)", name: "Cotton Candy" },
      { value: "radial-gradient(circle at 30% 30%, #FDEFF9 0%, #EC38BC 100%)", name: "Rose Quartz" },
      { value: "linear-gradient(135deg, #D9AFD9 0%, #97D9E1 100%)", name: "Soft Bloom" },
      { value: "linear-gradient(135deg, #FFF1EB 0%, #ACE0F9 100%)", name: "Morning Haze" },
      { value: "conic-gradient(from 270deg at 50% 50%, #FBC2EB, #A18CD1, #FBC2EB)", name: "Pastel Wheel" },
    ],
  },
  {
    id: "deep-moody",
    label: "Deep & Moody",
    gradients: [
      { value: "linear-gradient(160deg, #232526 0%, #414345 100%)", name: "Graphite" },
      { value: "linear-gradient(160deg, #141E30 0%, #243B55 100%)", name: "Twilight Steel" },
      { value: "radial-gradient(circle at 50% 30%, #3A1C71 0%, #D76D77 60%, #FFAF7B 100%)", name: "Aurora Night" },
      { value: "linear-gradient(160deg, #200122 0%, #6F0000 100%)", name: "Crimson Dusk" },
      { value: "linear-gradient(160deg, #0F0C29 0%, #302B63 50%, #24243E 100%)", name: "Cosmic" },
      { value: "conic-gradient(from 200deg at 50% 50%, #16222A, #3A6073, #16222A)", name: "Slate Wheel" },
    ],
  },
];

/**
 * Build a flat list of CSS gradient strings (for the picker's `gradients`
 * consumers, which iterate over plain strings). Legacy presets first (so their
 * stored values still highlight), then the new curated groups in order.
 *
 * De-duplicated case-insensitively so a curated value identical to a legacy one
 * doesn't render twice.
 */
const seen = new Set();
const flat = [];
const pushUnique = (value) => {
  const key = String(value).trim().toLowerCase();
  if (!key || seen.has(key)) return;
  seen.add(key);
  flat.push(value);
};

LEGACY_GRADIENTS.forEach(pushUnique);
GRADIENT_GROUPS.forEach((group) =>
  group.gradients.forEach((g) => pushUnique(g.value)),
);

/** Flat, de-duplicated CSS-gradient strings — the picker's preset source. */
export const CURATED_GRADIENTS = flat;

/** Quick tooltip lookup: gradient string -> human-friendly name (curated only). */
export const GRADIENT_NAME_BY_VALUE = GRADIENT_GROUPS.reduce((acc, group) => {
  group.gradients.forEach((g) => {
    acc[g.value] = g.name;
  });
  return acc;
}, {});
